import { useRef, useEffect } from "react";
import { Overlay } from "./Overlay";

interface ConfirmDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly message: string;
  readonly confirmLabel?: string;
  readonly cancelLabel?: string;
  readonly variant?: "danger" | "default";
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirmer",
  cancelLabel = "Annuler",
  variant = "default",
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) confirmRef.current?.focus();
  }, [open]);

  const confirmColor =
    variant === "danger"
      ? "bg-danger hover:opacity-80"
      : "bg-accent hover:opacity-80";

  return (
    <Overlay open={open} onClose={onCancel}>
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-message"
        className="bg-surface border border-border p-5 w-full max-w-[min(90vw,24rem)] shadow-xl rounded-2xl"
      >
        <h2
          id="confirm-dialog-title"
          className="text-base font-semibold text-ink mb-1.5"
        >
          {title}
        </h2>
        <p id="confirm-dialog-message" className="text-sm text-ink-muted mb-4">
          {message}
        </p>

        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-2 text-xs font-medium border border-border
              bg-surface text-ink hover:bg-surface-alt focus:outline-none
              focus:border-accent/60 rounded-md"
          >
            {cancelLabel}
          </button>
          <button
            ref={confirmRef}
            type="button"
            onClick={onConfirm}
            className={`px-3 py-2 text-xs font-medium ${confirmColor}
              text-white focus:outline-none focus:border-accent/60 rounded-md`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </Overlay>
  );
}
